import React from "react";
import { Link } from "react-router-dom";
import { Shield, FileText, Scale, AlertTriangle, Lock, CreditCard, Gavel, Users, Package, Globe } from "lucide-react";

// Section Component for consistent styling
const Section = ({ number, title, children }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-3">
      <span className="brand-gradient-text font-black">{number}.</span> {title}
    </h2>
    <div className="text-gray-400 space-y-3 leading-relaxed">
      {children}
    </div>
  </div>
);

// Legal Page Wrapper
const LegalPageWrapper = ({ title, icon: Icon, children }) => (
  <div className="min-h-screen pt-24 pb-12 px-4 bg-black">
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 brand-gradient-text px-4 py-2 rounded-full text-sm font-bold mb-4">
          <Icon size={16} className="text-orange-400" />
          <span>LEGAL</span>
        </div>
        <h1 className="text-3xl md:text-4xl font-black text-white"><span className="brand-gradient-text">{title}</span></h1>
      </div>
      
      {/* Content */}
      <div className="bg-black/60 rounded-2xl border border-white/10 p-6 md:p-8">
        {children}
      </div>
      
      {/* Footer Links */}
      <div className="flex flex-wrap justify-center gap-4 mt-8 text-sm">
        <Link to="/terms" className="text-gray-500 hover:brand-gradient-text hover:[-webkit-text-fill-color:transparent] transition-colors">Terms of Service</Link>
        <span className="text-gray-700">•</span>
        <Link to="/privacy" className="text-gray-500 hover:brand-gradient-text hover:[-webkit-text-fill-color:transparent] transition-colors">Privacy Policy</Link>
        <span className="text-gray-700">•</span>
        <Link to="/copyright" className="text-gray-500 hover:brand-gradient-text hover:[-webkit-text-fill-color:transparent] transition-colors">Copyright & DMCA</Link>
        <span className="text-gray-700">•</span>
        <Link to="/licensing" className="text-gray-500 hover:brand-gradient-text hover:[-webkit-text-fill-color:transparent] transition-colors">Licensing</Link>
      </div>
    </div>
  </div>
);

export const TermsPage = () => (
  <LegalPageWrapper title="Terms of Service" icon={FileText}>
    <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
      <p className="text-orange-400 text-sm font-medium">
        <strong>Effective Date:</strong> February 18, 2026 | <strong>Company:</strong> BLK PACKS™ (a DBA of Xcraft FunWorks LLC) | <strong>Website:</strong> www.blkpacks.fun
      </p>
    </div>
    
    <p className="text-gray-300 mb-8">
      These Terms of Service ("Terms") govern your access to and use of our website, products, digital assets, Swap Spot, and related services (collectively, the "Platform"). 
      <strong className="text-white"> By accessing or purchasing from the Platform, you agree to be legally bound by these Terms.</strong>
    </p>

    <Section number="1" title="COMPANY INFORMATION">
      <div className="p-4 bg-gray-800/50 border border-gray-700 rounded-xl">
        <p className="text-white font-bold mb-2">BLK PACKS™ is a DBA (Doing Business As) of Xcraft FunWorks LLC</p>
        <p className="text-gray-400">All references to "BLK PACKS," "we," "us," or "our" in these Terms refer to Xcraft FunWorks LLC operating under the BLK PACKS™ trade name.</p>
      </div>
    </Section>

    <Section number="2" title="ELIGIBILITY">
      <p>You must be at least 18 years old to purchase products or participate in digital asset trading activity. By using the Platform, you represent that you meet this requirement.</p>
    </Section>

    <Section number="3" title="REGISTERED GOODS & SERVICES">
      <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl mb-4">
        <p className="text-orange-400 font-bold mb-2">BLK PACKS™ TRADEMARK REGISTRATION</p>
        <p className="text-gray-400">The BLK PACKS™ mark is registered for the following goods and services:</p>
      </div>
      
      <div className="space-y-4">
        <div className="p-4 bg-gray-800/50 rounded-xl">
          <p className="text-white font-bold mb-2">International Class 016 - Physical Trading Cards</p>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>Collectible printed trading cards</li>
            <li>Physical graded collectible cards featuring historical, cultural, commemorative, and artistic themes</li>
          </ul>
        </div>
        
        <div className="p-4 bg-gray-800/50 rounded-xl">
          <p className="text-white font-bold mb-2">International Class 041 - Entertainment Experience</p>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>Providing online non-downloadable digital collectible trading card images for entertainment purposes</li>
            <li>Entertainment services featuring digital collectibles in the nature of images and video recordings</li>
            <li>Interactive entertainment experiences in the field of collectible trading cards</li>
          </ul>
        </div>
        
        <div className="p-4 bg-gray-800/50 rounded-xl">
          <p className="text-white font-bold mb-2">International Class 042 - Hosted Digital Collection Platform</p>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>Providing a hosted online platform for users to view and manage their digital collectible collections</li>
            <li>Software as a service (SaaS) featuring online non-downloadable software for digital collectible management</li>
            <li>Platform services for organizing and displaying user digital collectible portfolios</li>
          </ul>
        </div>
        
        <div className="p-4 bg-gray-800/50 rounded-xl">
          <p className="text-white font-bold mb-2">International Class 035 - Marketplace / Swap Environment</p>
          <ul className="list-disc list-inside space-y-1 text-gray-400">
            <li>Online marketplace services for collectors to swap digital collectibles within the platform</li>
            <li>Facilitating user-to-user exchanges of digital collectible items exclusively within the BLK PACKS™ ecosystem</li>
            <li>Platform-based trading environment for entertainment collectibles (no monetary exchange)</li>
          </ul>
        </div>
      </div>
    </Section>

    <Section number="4" title="PLATFORM COMPLIANCE STATEMENT">
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl mb-4">
        <p className="text-blue-400 font-bold mb-3">IMPORTANT PLATFORM DISCLOSURES</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300">
          <li><strong className="text-white">Digital collectibles are non-downloadable</strong> — All digital collectibles remain hosted on the BLK PACKS™ platform and cannot be downloaded, exported, or transferred outside the platform.</li>
          <li><strong className="text-white">Pack Points have no monetary value</strong> — Pack Points are engagement tokens used exclusively within the platform for entertainment purposes. They cannot be redeemed for cash, cryptocurrency, or any form of currency.</li>
          <li><strong className="text-white">Swaps occur within platform only</strong> — All collectible exchanges through the Swap Spot are facilitated exclusively within the BLK PACKS™ ecosystem. No external transfers are supported.</li>
          <li><strong className="text-white">No financial services provided</strong> — BLK PACKS™ is an entertainment platform. We do not provide banking, investment, lending, or any financial services.</li>
        </ul>
      </div>
      
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
        <p className="text-red-400 font-bold mb-2">WE DO NOT:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Operate as a financial institution</li>
          <li>Provide stored value or payment services</li>
          <li>Offer investment products or advice</li>
          <li>Facilitate cryptocurrency or blockchain transactions</li>
          <li>Allow withdrawal of points or collectibles to external systems</li>
        </ul>
      </div>
    </Section>

    <Section number="5" title="PRODUCTS & SERVICES">
      <p>We offer:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Professionally graded collectible printed trading cards in four rarity tiers: Common, Rare, Epic, and Legendary (Class 16)</li>
        <li>Limited-edition collectible releases ("Drops") with varying rarity distributions</li>
        <li>Online non-downloadable digital collectible trading card images for entertainment purposes (Class 41)</li>
        <li>Hosted digital collection management platform ("Locker") (Class 42)</li>
        <li>Platform-based Swap Spot marketplace for collector-to-collector exchanges (Class 35)</li>
        <li>Platform-based Pack Points engagement system</li>
        <li>RUN IT 1V1 — a peer-to-peer dice game of skill where two users stake same-rarity Digital Collectibles; the system auto-resolves a classic craps sequence and transfers both stakes to the winner (see Section 20)</li>
        <li>Augmented Reality (AR) experiences — scan physical AR-enabled cards to view 3D models anchored in your environment via Apple AR Quick Look or Google Scene Viewer</li>
        <li><strong className="text-white">Monster Meter AI / Scan AI</strong> — an artificial-intelligence card identification and valuation service that analyzes user-submitted photographs of trading card backsides to identify the player, year, set, parallel, serial number, and to provide estimated market value ranges. For PSA-graded cards, the service cross-references the public PSA cert lookup API to display the verified grade and population report. All AI-generated identifications and valuations are estimates for informational purposes only and are not guarantees of market price, grade, or authenticity (see Section 14).</li>
        <li>Tap Trade — a real-time, 6-digit-code card-swapping system for live events and in-person meetups</li>
        <li>Live Event Mode — a dedicated full-screen trading and leaderboard experience designed for drops and collector events</li>
        <li>Apple Wallet loyalty passes for collector identification</li>
        <li>Serial number claims — verified first-rip collectibles with unique serial identifiers</li>
        <li>Native mobile applications on iOS (App Store) and Android (Google Play)</li>
      </ul>
      <p className="mt-3">All products are private collectibles for entertainment purposes unless expressly stated otherwise.</p>
    </Section>

    <Section number="6" title="NOT LEGAL TENDER · NOT BULLION · NOT AN INVESTMENT">
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
        <p className="text-red-400 font-bold mb-2">ALL PRODUCTS SOLD ARE:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>NOT legal tender</li>
          <li>NOT government-issued currency</li>
          <li>NOT backed by any government</li>
          <li>NOT affiliated with any central bank</li>
          <li>NOT authorized for circulation as money</li>
        </ul>
        <p className="mt-3 text-gray-500 text-sm">Any resemblance to official currency format is artistic and commemorative only.</p>
      </div>

      <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
        <p className="text-amber-400 font-bold mb-3">GOLD & SILVER LINES — SOLD AS COLLECTIBLES, NOT AS BULLION</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300">
          <li><strong className="text-white">BLK PACKS Gold and Silver lines are premium hobby collectibles.</strong> Each note and card is a premium physical collectible — sold, priced, and marketed as a collectible, not as a bullion coin, bullion bar, or investment-grade precious-metal product.</li>
          <li><strong className="text-white">You're collecting the Moments.</strong> You buy the physical BLK PACKS card or note — and what you're collecting is the Moment it captures, its artwork, serial number, and rarity tier. Our materials, blend, construction, finishing, and manufacturing methods are proprietary, and are not an invitation to melt, refine, or reverse-engineer the collectible.</li>
          <li><strong className="text-white">Market value is collector-driven.</strong> The value of a BLK PACKS collectible is determined by collector demand, rarity, edition size, condition, and ecosystem utility. It is not set by, floored by, or redeemable for any commodity spot or melt price.</li>
          <li><strong className="text-white">Not a security, not a financial instrument, not an investment.</strong> Buying a BLK PACKS collectible does not entitle you to dividends, interest, profit-sharing, voting rights, or any return on capital. Collector market value can rise, fall, or go to zero — buy what you love, not what you can't afford to lose.</li>
        </ul>
      </div>

      <div className="mt-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl" data-testid="tos-proprietary-clause">
        <p className="text-purple-300 font-bold mb-3">PROPRIETARY DESIGN — NO MELTING, REFINING, OR REVERSE-ENGINEERING</p>
        <ul className="list-disc list-inside space-y-2 text-gray-300">
          <li><strong className="text-white">Our metal blend and manufacturing process are protected trade secrets.</strong> The precise way genuine gold and silver are blended, layered, and finished into each BLK PACKS collectible is proprietary. In other words, that's our Michael Jordan secret sauce — and we're keeping the recipe in the vault.</li>
          <li><strong className="text-white">Prohibited conduct.</strong> Melting, refining, extracting, destructively assaying, or reverse-engineering any BLK PACKS product is strictly prohibited and may constitute trade-secret misappropriation under applicable law, including the Defend Trade Secrets Act (18 U.S.C. § 1836). A BLK PACKS collectible is worth more intact than melted — nobody melts a collectible gold note to get at the metal.</li>
          <li><strong className="text-white">Consequences.</strong> Any attempt to melt, refine, extract, destructively assay, or reverse-engineer a BLK PACKS collectible voids its certificate of authenticity, its EMERGE ledger provenance, all ecosystem utility tied to its serial number (locker entry, AR experience, Swap Spot history), and any buyback eligibility. We make BLK PACKS. We know how they're made. The secret sauce stays secret.</li>
          <li><strong className="text-white">Your agreement.</strong> By purchasing any BLK PACKS product, you acknowledge these terms and agree not to melt, refine, extract, destructively assay, or reverse-engineer it, and you confirm your purchase is made for collecting, display, trade, and ecosystem enjoyment.</li>
        </ul>
      </div>

      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
        <p className="text-blue-400 font-bold mb-2">WHAT GIVES A BLK PACKS COLLECTIBLE ITS VALUE</p>
        <p className="text-gray-300 mb-2">Value comes from the complete ecosystem:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Limited production runs</li>
          <li>Serial-numbered collectibles with on-chain (EMERGE ledger) provenance</li>
          <li>Exclusive artwork and designs</li>
          <li>Digital Moments That Matter</li>
          <li>Augmented Reality (AR) experiences</li>
          <li>Community and platform utility (Locker, Swap Spot, RUN IT, Live Breaks, BLK PAY)</li>
          <li>Collector demand and rarity</li>
        </ul>
      </div>
    </Section>

    <Section number="7" title="COLLECTIBLE TIERS & GRADING">
      <p>Our collectible cards are categorized into four rarity tiers:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li><strong className="text-orange-400">Legendary</strong> — Ultra-rare premium cards with the highest collectible value</li>
        <li><strong className="text-orange-400">Epic</strong> — Limited-edition cards with elevated rarity</li>
        <li><strong className="text-blue-400">Rare</strong> — Above-average rarity with distinct design elements</li>
        <li><strong className="text-gray-300">Common</strong> — Standard-tier cards included in every drop</li>
      </ul>
      <div className="mt-4 p-4 bg-gray-800/50 rounded-xl">
        <p className="text-white font-bold mb-2">Disclosures:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>All collectibles are professionally graded and authenticated</li>
          <li>Products are collectibles for entertainment purposes, NOT financial instruments</li>
          <li>Collectible values may fluctuate based on market demand</li>
          <li>Collectible premiums are based on rarity and demand</li>
          <li>Future value is not guaranteed</li>
        </ul>
      </div>
    </Section>

    <Section number="7" title="PURCHASE MODEL - NOT STORED VALUE">
      <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl mb-4">
        <p className="text-blue-400 font-bold mb-2">FIXED PURCHASE TIERS</p>
        <p className="text-gray-400">BLK PACKS™ offers customers fixed purchase tiers (e.g., $25, $50, $100, $500, up to $999). These are <strong className="text-white">one-time retail purchase amounts</strong> for collectible product packages.</p>
      </div>
      <p className="font-bold text-white mb-2">Customers are NOT "topping up" a monetary wallet or stored balance.</p>
      <p className="mb-3">The selected amount represents the purchase of a designated collectible product tier and associated digital companion content.</p>
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
        <p className="text-red-400 font-bold mb-2">WE DO NOT PROVIDE:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Stored monetary balances</li>
          <li>Reloadable accounts</li>
          <li>Transferable credits</li>
          <li>Withdrawable funds</li>
          <li>Redeemable cash equivalents</li>
          <li>Peer-to-peer payments</li>
        </ul>
      </div>
      <p className="mt-3 text-orange-400 font-medium">The purchase amount is immediately applied to the specific product tier selected and does not remain as an open or redeemable stored value.</p>
    </Section>

    <Section number="6" title="CHALLENGE POINTS - ENGAGEMENT UNITS ONLY">
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-4">
        <p className="text-orange-400 font-bold mb-2">CHALLENGE POINTS ARE NOT CURRENCY</p>
        <p className="text-gray-400">Pack Points are <strong className="text-white">promotional engagement units only</strong> with NO monetary value whatsoever.</p>
      </div>
      
      <p className="font-bold text-white mb-2">How Pack Points Work:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Earned through product purchases and platform participation</li>
        <li>Used <strong className="text-orange-400">ONLY</strong> to enter platform challenges</li>
        <li>Challenge winners receive physical collectible cards + digital certificates</li>
      </ul>
      
      <p className="font-bold text-red-400 mb-2">Pack Points CANNOT:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4 text-red-300">
        <li>Be converted into money or cash equivalents</li>
        <li>Be used to reduce the cost of future purchases</li>
        <li>Be withdrawn or transferred for monetary value</li>
        <li>Be redeemed for currency (fiat or crypto)</li>
        <li>Represent stored monetary funds</li>
      </ul>
      
      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
        <p className="text-green-400 font-bold mb-2">All Challenge Rewards Are:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Tangible physical collectible cards</li>
          <li>Digital certificates of authenticity</li>
          <li>Items that exist entirely within BLK PACKS™ collectibles ecosystem</li>
        </ul>
      </div>
      
      <p className="mt-4 text-gray-500 text-sm">Points function similarly to arcade tokens or game tickets—they enable participation in activities but have no value outside the platform.</p>
    </Section>

    <Section number="7" title="DIGITAL ASSETS & COLLECTIBLES">
      <p><strong className="text-white">Digital collectibles:</strong></p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Are not securities</li>
        <li>Are not financial instruments</li>
        <li>Are not investment contracts</li>
        <li>Do not represent equity</li>
        <li>Are not legal tender</li>
        <li>Are digital companion items to physical collectibles</li>
      </ul>
      <p className="mt-3">We reserve the right to modify or discontinue digital features at any time.</p>

      <div className="mt-4 p-4 bg-orange-500/5 border border-orange-500/20 rounded-xl">
        <p className="text-white font-bold mb-2">Hobby Filters (4D AR Experiences):</p>
        <p className="text-gray-400">
          Hobby Filters are unlockable BLK PACK 4D AR experiences earned through Hobby Drops.
          Use them in challenges, rankings, and interactive events across the platform.
          Filters are <span className="text-white font-bold">non-transferable platform rewards</span> with
          <span className="text-white font-bold"> no cash value</span> and <span className="text-white font-bold">cannot be sold or traded</span>.
        </p>
      </div>
    </Section>

    <Section number="8" title="PUBLIC DOMAIN & RIGHT OF PUBLICITY">
      <p>Our collectibles may depict historical individuals whose likenesses are in the public domain.</p>
      <p className="mt-3">Where postmortem publicity rights have expired:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>No estate license is required</li>
        <li>No endorsement is implied</li>
      </ul>
      <p className="mt-3">We do not claim affiliation with estates, museums, foundations, or government agencies. Living individuals' likenesses are not used without authorization.</p>
    </Section>

    <Section number="9" title="INTELLECTUAL PROPERTY">
      <p>All original artwork, designs, logos, digital assets, and branding are the property of the Company. You may not reproduce, duplicate, distribute, or exploit any content without written consent.</p>
    </Section>

    <Section number="10" title="NO INVESTMENT ADVICE">
      <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
        <p className="text-orange-400 font-bold mb-2">Nothing on the Platform constitutes:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Investment advice</li>
          <li>Financial advice</li>
          <li>Legal advice</li>
          <li>Tax advice</li>
        </ul>
        <p className="mt-3 text-gray-500">Purchases are made at your sole discretion and risk. You are responsible for conducting your own due diligence.</p>
      </div>
    </Section>

    <Section number="11" title="SPECULATIVE VALUE & MARKET RISK">
      <p>You acknowledge that:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Collectible values fluctuate</li>
        <li>Collectible markets are speculative</li>
        <li>Digital asset markets are volatile</li>
        <li>Swap Spot liquidity is not guaranteed</li>
      </ul>
      <p className="mt-3 font-bold text-white">We make no guarantees regarding appreciation, resale value, or future demand.</p>
    </Section>

    <Section number="12" title="REFUNDS & DISPUTE POLICY">
      <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl mb-4">
        <p className="text-green-400 font-bold mb-2">REFUND POLICY</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Refund requests are reviewed promptly through our customer support team</li>
          <li>Orders that have not yet shipped are eligible for cancellation and full refund</li>
          <li>If a physical product has shipped, customers may request a return in accordance with our published return policy</li>
          <li>Refunds are issued back to the original payment method</li>
        </ul>
      </div>
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-4">
        <p className="text-red-400 font-bold mb-2">IMPORTANT LIMITATIONS</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>We do NOT issue cash alternatives, credits with monetary value, or transferable balances</li>
          <li>Digital companion items and platform participation points have NO monetary value and are non-withdrawable</li>
          <li>If a qualifying refund is approved, associated digital access and participation points are deactivated</li>
        </ul>
      </div>
      <p className="font-bold text-white mb-2">Disputes & Chargebacks:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>We actively monitor transactions using fraud detection tools</li>
        <li>High-value or flagged transactions may be manually reviewed</li>
        <li>In the event of a chargeback, we respond within required timeframes with order confirmation, IP logs, customer communications, and proof of delivery when applicable</li>
        <li>We track dispute ratios to ensure they remain within acceptable thresholds</li>
      </ul>
    </Section>

    <Section number="13" title="NO REFUNDS FOR MARKET FLUCTUATIONS">
      <p className="font-bold text-white">All sales are final unless otherwise required by law.</p>
      <p className="mt-3">We do not provide refunds or compensation based on:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Collectible market value changes</li>
        <li>Market volatility</li>
        <li>Swap Spot value shifts</li>
        <li>Perceived value decline</li>
      </ul>
      <p className="mt-3 text-orange-400">Market risk is assumed entirely by the purchaser.</p>
    </Section>

    <Section number="14" title="ORDER ACCEPTANCE & CANCELLATION">
      <p>We reserve the right to:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Refuse or cancel orders</li>
        <li>Limit quantities</li>
        <li>Correct pricing errors</li>
        <li>Cancel transactions suspected of fraud</li>
      </ul>
    </Section>

    <Section number="15" title="AUGMENTED REALITY (AR) FEATURES">
      <p>BLK PACKS™ offers AR experiences on select collectible cards. By using AR features, you acknowledge:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>AR requires camera access on your device — the platform does not record, store, or transmit camera imagery</li>
        <li>3D models displayed in AR are non-downloadable digital assets owned by BLK PACKS™</li>
        <li>AR experiences are accessed via Apple AR Quick Look (iOS) or Google Scene Viewer (Android) through the native app, or via the web using Google model-viewer</li>
        <li>AR view analytics (device type, duration, card viewed) are collected to improve the experience</li>
        <li>Physical AR-enabled cards contain QR codes that link to BLK PACKS™ hosted AR pages — these QR codes are not transferable licenses</li>
      </ul>
    </Section>

    <Section number="16" title="TAP TRADE & LIVE EVENT MODE">
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-4">
        <p className="text-orange-400 font-bold mb-2">TRADING IS NON-MONETARY</p>
        <p className="text-gray-400">All trades conducted through Tap Trade, Event Trade, or Remote Trade are <strong className="text-white">collector-to-collector exchanges of digital collectibles</strong> with no monetary value exchanged.</p>
      </div>
      <p className="font-bold text-white mb-2">Trade Types:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li><strong className="text-white">Tap Trade</strong> — generates a 6-digit code valid for 5 minutes; both parties select a card and the swap executes instantly</li>
        <li><strong className="text-white">Event Trade (Scan-to-Match)</strong> — automatic matching of pending trades at live events; 15-minute expiry</li>
        <li><strong className="text-white">Remote Trade</strong> — asynchronous trade request sent to another collector; 24-hour expiry</li>
      </ul>
      <p>Once a trade is completed, ownership of the digital collectibles is permanently transferred between the two accounts. Completed trades are <strong className="text-white">final and irreversible</strong>.</p>
      <p className="mt-3"><strong className="text-white">Live Event Mode</strong> is a full-screen trading interface designed for live drops and collector meetups. It includes a real-time leaderboard, Tap Trade code display, and rapid card selection. Live Event Mode does not alter the trading rules above.</p>
    </Section>

    <Section number="20" title="RUN IT 1V1 — GAME OF SKILL">
      <div className="p-4 bg-orange-500/10 border border-orange-500/40 rounded-xl mb-4">
        <p className="text-orange-400 font-black mb-2">RUN IT 1V1 IS A GAME OF SKILL WITHIN A CLOSED-LOOP ENTERTAINMENT ECOSYSTEM</p>
        <p className="text-gray-300 text-sm leading-relaxed">
          RUN IT 1V1 is a peer-to-peer dice-resolution mode in which two users each stake one Digital Collectible of matching rarity. The system auto-plays a classic craps sequence (2d6) and atomically transfers both staked Digital Collectibles to the winning account. No cash, cryptocurrency, stored value, or external consideration is ever staked or awarded. Participants may only stake Digital Collectibles they already own within BLK PACKS™. RUN IT 1V1 is intended solely as an <strong className="text-white">entertainment feature of skill and strategy</strong> within the BLK PACKS™ closed-loop environment.
        </p>
      </div>
      <p className="font-bold text-white mb-2">Rules:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>Both participants must stake a Digital Collectible of the same rarity tier (Common / Rare / Epic / Legendary)</li>
        <li>The system randomly designates one participant as the "Shooter" and rolls a 2-die sequence on the server</li>
        <li>Come-out roll: 7 or 11 = Shooter wins; 2, 3, or 12 = Shooter loses; any other value sets the "Point"</li>
        <li>Point phase: rolling the Point again = Shooter wins; rolling a 7 = Shooter loses; other rolls continue</li>
        <li>On resolution, both staked Digital Collectibles are atomically transferred to the winning user's Locker</li>
        <li>Participants may cancel a match only while it remains in the <em>waiting</em> state; once joined, matches are final and irreversible</li>
        <li>House Bot: to maintain match availability, BLK PACKS™ may operate an official house matchmaker ("House Bot") that automatically joins waiting matches after a configurable wait period. Dice remain server-generated and random; only the Shooter designation may be tilted in favor of the house. Users retain a meaningful chance to win in every match. When a user defeats the House Bot, their staked Digital Collectible is returned to their Locker (the house does not transfer an inventory card); when the House Bot wins, the user's staked Digital Collectible is transferred to the BLK PACKS™ house inventory.</li>
        <li>No player-controlled input influences the dice; the result is determined purely by server-side random generation after stakes are committed</li>
      </ul>
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
        <p className="text-red-400 font-bold mb-2">NOT GAMBLING / NOT A GAME OF CHANCE:</p>
        <p className="text-gray-300 text-sm">
          RUN IT 1V1 awards and risks only Digital Collectibles that have no guaranteed monetary value and exist exclusively within the BLK PACKS™ closed-loop environment. Digital Collectibles cannot be withdrawn, converted, or redeemed for cash, cryptocurrency, stored value, or other monetary instruments. Pack Points earned or spent on the Platform likewise have no monetary value and exist exclusively within the BLK PACKS™ closed-loop environment; however Pack Points may be used by users to purchase the right to Rip physical, professionally graded Gold and Silver packs which are then fulfilled and shipped or emailed to the user in accordance with applicable shipping policies. Because no monetary consideration is wagered and no monetary prize is awarded, RUN IT 1V1 is offered as an entertainment game of skill and is not intended to constitute gambling under any applicable law. Participation is available subject to the age rating assigned to the BLK PACKS™ mobile applications on the Apple App Store and Google Play Store (currently <strong className="text-white">9+</strong>), and to jurisdictions where such peer-to-peer entertainment features are lawful. It is your responsibility to ensure that participation complies with the laws of your jurisdiction; BLK PACKS™ reserves the right to restrict or disable RUN IT 1V1 in any jurisdiction at any time.
        </p>
      </div>
    </Section>

    <Section number="21" title="GOLD & SILVER PHYSICAL CARDS">
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl mb-4">
        <p className="text-orange-400 font-bold mb-2">PHYSICAL PACK SPECIFICATIONS</p>
        <p className="text-gray-300 text-sm">
          Physical BLK PACKS™ cards shipped to users are premium <strong className="text-white">Gold line</strong> or <strong className="text-white">Silver line</strong> hobby collectibles per product specification, individually graded, and accompanied by a Certificate of Authenticity (COA) and a unique Verification ID visible in the EMERGE public ledger. All Digital Collectibles created from physical drops receive a digital twin which includes the AR experience and may be used in RUN IT 1V1 (where enabled by rarity tier).
        </p>
      </div>
      <p>By purchasing a physical pack, you receive: (a) the professionally graded physical card(s), (b) the matching Digital Collectible(s) in your Locker, (c) access to AR Quick Look for AR-enabled cards, and (d) eligibility to stake the Digital Collectible(s) in RUN IT 1V1 per Section 20. Physical packs are fulfilled by shipment to the address you provide or, in the case of eligible promotional packs, by email delivery of a unique Claim Code that you may redeem for physical fulfillment.</p>
    </Section>

    <Section number="17" title="ACCOUNT DELETION & DATA REMOVAL">
      <p>You may delete your account at any time from your Profile settings. Upon account deletion:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Your account is <strong className="text-white">permanently and instantly deactivated</strong></li>
        <li>All digital collectibles in your Locker are forfeited and cannot be recovered</li>
        <li>Pack Points balance is permanently forfeited (no cash value)</li>
        <li>Trade history and activity records are retained for fraud prevention and legal compliance as required by law</li>
        <li>Personal information is deleted in accordance with our Privacy Policy retention schedule</li>
        <li>Pending trades are automatically canceled</li>
      </ul>
      <p className="mt-3 text-red-400 font-medium">Account deletion is irreversible. This action cannot be undone.</p>
    </Section>

    <Section number="18" title="SERIAL NUMBER CLAIMS & FIRST RIP">
      <p>Select collectible drops include a "First Rip" experience, where the first user to open a sealed pack receives a unique serial-numbered collectible. Serial numbers are:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Assigned sequentially and permanently linked to the claiming account</li>
        <li>Displayed on the user's profile as proof of early participation</li>
        <li>Non-transferable between accounts (serial stays with the original ripper)</li>
        <li>Not redeemable for cash, points, or any monetary equivalent</li>
      </ul>
    </Section>

    <Section number="19" title="REPORT ABUSE">
      <p>Users may report abusive content, fraudulent trade behavior, or intellectual property violations through the in-app reporting system. Upon receiving a report, BLK PACKS™ will:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Review all reports within 24-48 business hours</li>
        <li>Take appropriate action including content removal, account warnings, or account suspension</li>
        <li>Notify the reporting user of the outcome when required by policy</li>
      </ul>
      <p className="mt-3">Filing a false or malicious report may result in action against the reporting account.</p>
    </Section>

    <Section number="20" title="DMCA POLICY">
      <p>We comply with the Digital Millennium Copyright Act. To submit a claim, provide:</p>
      <ol className="list-decimal list-inside ml-4 space-y-1">
        <li>Your name and contact information</li>
        <li>Identification of the copyrighted work</li>
        <li>Identification of the allegedly infringing material</li>
        <li>Good-faith belief statement</li>
        <li>Statement under penalty of perjury</li>
        <li>Signature</li>
      </ol>
      <p className="mt-3">We may remove content upon valid notice. Counter-notifications may be submitted in accordance with law.</p>
    </Section>

    <Section number="13" title="LIMITATION OF LIABILITY">
      <div className="p-4 bg-gray-800/50 rounded-xl">
        <p className="text-white font-bold mb-2">To the fullest extent permitted by law, the Company shall not be liable for:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400">
          <li>Market losses</li>
          <li>Collectible market value fluctuations</li>
          <li>Digital asset volatility</li>
          <li>Indirect damages</li>
          <li>Consequential damages</li>
          <li>Lost profits</li>
          <li>Loss of opportunity</li>
        </ul>
        <p className="mt-3 text-orange-400 text-sm">Total liability shall not exceed the amount paid for the specific product giving rise to the claim.</p>
      </div>
    </Section>

    <Section number="14" title="INDEMNIFICATION">
      <p>You agree to indemnify and hold harmless the Company, its owners, affiliates, officers, and employees from any claims arising out of:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Your misuse of the Platform</li>
        <li>Your violation of these Terms</li>
        <li>Your resale activities</li>
        <li>Your Swap Spot transactions</li>
      </ul>
    </Section>

    <Section number="15" title="TERMINATION">
      <p>We reserve the right to suspend accounts, remove digital assets, restrict Swap Spot access, and terminate users at our sole discretion if violations occur.</p>
    </Section>

    <Section number="16" title="ARBITRATION & CLASS ACTION WAIVER">
      <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
        <p className="text-orange-400 font-bold mb-2">Any dispute shall be resolved through binding arbitration in the State of Colorado.</p>
        <p className="text-white mt-2">You waive the right to:</p>
        <ul className="list-disc list-inside space-y-1 text-gray-400 mt-2">
          <li>Jury trial</li>
          <li>Participate in class action lawsuits</li>
          <li>Join consolidated claims</li>
        </ul>
        <p className="mt-3 text-gray-500">Disputes must be brought individually.</p>
      </div>
    </Section>

    <Section number="17" title="GOVERNING LAW">
      <p>These Terms shall be governed by the laws of the State of Colorado, without regard to conflict-of-law principles.</p>
    </Section>

    <Section number="18" title="MODIFICATIONS">
      <p>We reserve the right to modify these Terms at any time. Continued use of the Platform constitutes acceptance of updated Terms.</p>
    </Section>

    <Section number="19" title="CHARGEBACK & PAYMENT ABUSE POLICY">
      <h3 className="text-white font-bold mt-4 mb-2">19.1 Payment Authorization</h3>
      <p>By submitting payment, you represent and warrant that you are authorized to use the payment method provided, the billing information is accurate, and you are not initiating a purchase with intent to dispute or reverse payment.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">19.2 Fraudulent Chargebacks</h3>
      <p>Initiating a chargeback without first contacting the Company for resolution may be considered payment abuse. We reserve the right to:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Contest any chargeback with supporting documentation</li>
        <li>Submit delivery confirmation, order records, and digital logs to the payment processor</li>
        <li>Report fraudulent activity to payment processors and financial institutions</li>
        <li>Permanently ban associated accounts</li>
        <li>Restrict future purchases</li>
        <li>Pursue civil remedies where applicable</li>
      </ul>
      
      <h3 className="text-white font-bold mt-4 mb-2">19.3 Digital Asset & Downloadable Products</h3>
      <p>Where digital assets or platform-based benefits have been delivered, activated, or accessed, a chargeback may constitute fraudulent use of services. Access logs, blockchain records (if applicable), system timestamps, and IP address records may be used as evidence of fulfillment.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">19.4 Administrative Fees</h3>
      <p>We reserve the right to impose a reasonable administrative fee for payment disputes initiated in bad faith.</p>
    </Section>

    <Section number="20" title="PRE-ORDER & FULFILLMENT POLICY">
      <h3 className="text-white font-bold mt-4 mb-2">20.1 Pre-Orders</h3>
      <p>Certain products may be offered as pre-orders. By placing a pre-order, you acknowledge:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>The item is not yet manufactured or shipped</li>
        <li>Estimated ship dates are approximate and subject to change</li>
        <li>Production timelines may be impacted by supply chain delays or manufacturing schedules</li>
      </ul>
      <p className="mt-2">Pre-orders reserve your allocation in a limited release.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">20.2 Fulfillment Timeframes</h3>
      <p>Delivery timelines may vary due to manufacturing schedules, quality control processes, shipping carrier delays, and force majeure events. Estimated fulfillment dates are not guaranteed delivery dates.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">20.3 No Cancellation After Production Lock</h3>
      <p>Once production has commenced, orders may not be canceled and deposits may be non-refundable.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">20.4 Shipping Risk</h3>
      <p>Risk of loss transfers to the purchaser upon delivery to the carrier unless otherwise required by law. Customers are responsible for ensuring correct shipping information at checkout.</p>
    </Section>

    <Section number="21" title="ANTI-FRAUD & COMPLIANCE POLICY">
      <h3 className="text-white font-bold mt-4 mb-2">21.1 Transaction Integrity</h3>
      <p>We reserve the right to review any transaction for suspicious purchasing behavior, chargeback disputes, Swap Spot trades, and regulatory compliance purposes. Review may result in order cancellation or account suspension where integrity concerns arise.</p>

      <h3 className="text-white font-bold mt-4 mb-2">21.1.a Public Authenticity Ledger — EMERGE</h3>
      <p>Every collectible issued on the BLK PACKS platform is recorded on <a href="/verify" className="text-orange-400 underline hover:brand-gradient-text">EMERGE</a>, our off-chain public verification ledger. Each record carries a deterministic Verification ID (format: <code className="text-orange-400">COA-&lt;CARD&gt;-&lt;YEAR&gt;-BLK&lt;SERIAL&gt;</code>) that anyone can look up at <a href="/verify" className="text-orange-400 underline hover:brand-gradient-text">blkpacks.fun/verify</a> without an account. EMERGE is the authoritative source of authenticity for all digital and digital + physical cards issued through the platform.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">21.2 Anti-Fraud Monitoring</h3>
      <p>We actively monitor transactions for payment fraud, reshipping schemes, card testing, automated purchasing activity, and trading manipulation. We reserve the right to cancel suspicious orders, freeze accounts pending investigation, refuse service, and report fraudulent activity to appropriate authorities.</p>
      
      <h3 className="text-white font-bold mt-4 mb-2">21.3 Compliance With Law</h3>
      <p>We comply with applicable anti-money laundering (AML) regulations, fraud prevention standards, payment processor rules, and export and trade restrictions. Users agree not to use the Platform for unlawful financial activity.</p>
    </Section>

    <Section number="22" title="RESERVATION OF RIGHTS">
      <p>The Company reserves all rights not expressly granted herein. Nothing in these Terms shall be interpreted as limiting the Company's ability to protect its intellectual property, enforce platform integrity, defend against fraudulent claims, or protect against financial abuse.</p>
    </Section>

    <Section number="23" title="AR ENHANCEMENT PLATFORM — THIRD-PARTY IP DISCLAIMER">
      <div className="p-4 bg-orange-500/10 border-2 border-orange-500/40 rounded-xl mb-4">
        <p className="text-orange-400 font-black mb-2 text-base">BLK PACKS™ IS AN AR COMPANION / ENTERTAINMENT SOFTWARE PLATFORM</p>
        <p className="text-gray-200 text-sm leading-relaxed">
          BLK PACKS™ is <strong className="text-white">interactive entertainment software</strong> that provides augmented reality (AR) animations, hologram-style transformations, particle effects, music, and stylized 3D environments to enhance the user's experience with collectible trading cards the user already owns. The value we provide is the <strong className="text-white">software experience itself</strong> — not the underlying card art, athlete likeness, team mark, league mark, or any other third-party intellectual property that may appear on a physical card the user scans.
        </p>
      </div>

      <h3 className="text-white font-bold mt-4 mb-2">23.1 What BLK PACKS™ Does</h3>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Authenticates that a user-owned physical or digital card exists and is registered in our EMERGE ledger</li>
        <li>Launches a software-driven AR scene that may include stylized animations, comic/anime-inspired transformations, portals, fire/water/cosmic effects, original soundtracks, original UI overlays, and original 3D environments</li>
        <li>Provides original artistic compositions, original music, original sound design, original UI/UX, and original 3D scene authoring</li>
      </ul>

      <h3 className="text-white font-bold mt-4 mb-2">23.2 What BLK PACKS™ Does NOT Do</h3>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>We do <strong className="text-white">NOT</strong> issue, license, sublicense, redistribute, sell, or claim ownership of any third-party athlete name, image, likeness (NIL), team mark, league mark, broadcaster mark, manufacturer mark, or any other third-party intellectual property that may appear on a physical card a user happens to scan</li>
        <li>We do <strong className="text-white">NOT</strong> claim affiliation with, endorsement by, sponsorship by, or partnership with the NBA, NFL, MLB, NHL, MLS, NCAA, Topps, Panini, Upper Deck, Bowman, Fleer, Donruss, Score, or any other professional sports league, players' association, team, broadcaster, manufacturer, trading-card publisher, or athlete</li>
        <li>We do <strong className="text-white">NOT</strong> reproduce, redistribute, or commercially exploit any underlying third-party card art, athlete likeness, or sports IP. Our AR scenes are <strong className="text-white">transformative artistic compositions</strong> (stylized hologram silhouettes, anime/comic transformations, portal effects, cosmic environments, original music, BLK PACKS™ branded UI) that constitute original software-driven entertainment experiences</li>
        <li>Any reference to a real athlete or team is purely <strong className="text-white">incidental and descriptive</strong> — e.g., to allow the user to identify which card they already own — and is presented as the user's own property, not BLK PACKS™ inventory</li>
      </ul>

      <h3 className="text-white font-bold mt-4 mb-2">23.3 User-Owned Cards / Bring-Your-Own-IP</h3>
      <p>The cards a user scans, photographs, or registers into the BLK PACKS™ platform are presumed to be <strong className="text-white">already lawfully owned by the user</strong> (a physical card legally purchased on the secondary market, a digital card already in their possession, or a card minted by BLK PACKS™). BLK PACKS™ provides software that <em>recognizes and enhances</em> what the user already owns. We are not the issuer of any third-party card's underlying intellectual property.</p>

      <h3 className="text-white font-bold mt-4 mb-2">23.4 Transformative / Fair-Use Posture</h3>
      <p>To the maximum extent permitted by law, BLK PACKS™ asserts that any incidental appearance of a third-party trademark or copyrighted card art on a user-scanned card, within the context of an AR enhancement experience, constitutes <strong className="text-white">nominative fair use</strong> (the user needs to identify their own card to launch the AR scene) and that BLK PACKS™'s original software-generated AR scenes (animations, transformations, soundtracks, environments) are <strong className="text-white">transformative original works</strong> that do not compete with, replace, or commercially substitute for the original card art.</p>

      <h3 className="text-white font-bold mt-4 mb-2">23.5 Trademark Attribution</h3>
      <p>All third-party trademarks, service marks, trade names, product names, athlete names, team names, and league names that may appear on a user-scanned card are the property of their respective owners. Their incidental presence in the BLK PACKS™ AR pipeline is for the purpose of identifying the user's own existing collectible and does not imply any commercial relationship, endorsement, or sponsorship.</p>

      <h3 className="text-white font-bold mt-4 mb-2">23.6 IP Owner Reporting</h3>
      <p>If you are an intellectual property owner and believe a BLK PACKS™ AR experience is overstepping the transformative/companion-software posture described above with respect to your specific IP, please contact <a href="mailto:dmca@blkpacks.fun" className="text-orange-400 underline">dmca@blkpacks.fun</a>. We will review in good faith and may modify, gate, or disable the affected AR scene.</p>

      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl mt-4">
        <p className="text-red-400 font-bold mb-2">BOTTOM LINE</p>
        <p className="text-gray-300 text-sm">
          BLK PACKS™ sells you <strong className="text-white">interactive entertainment software</strong> and BLK PACKS™-authored digital + physical collectibles. We do <strong className="text-white">not</strong> sell, sublicense, or claim ownership of any third-party sports IP that may be visible on a card you happen to scan. All third-party marks belong to their respective owners.
        </p>
      </div>
    </Section>

    <div className="mt-8 p-4 bg-gray-800/50 rounded-xl text-center">
      <p className="text-gray-500 text-sm">
        <strong className="text-white">BLK PACKS™</strong><br />
        205 North Michigan Avenue, Suite 810, Chicago, IL 60601<br />
        Questions? Contact: support@blkpacks.fun
      </p>
    </div>
  </LegalPageWrapper>
);

export const PrivacyPage = () => (
  <LegalPageWrapper title="Privacy Policy" icon={Shield}>
    <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
      <p className="text-orange-400 text-sm font-medium">
        <strong>Effective Date:</strong> February 18, 2026 | Your privacy is important to us.
      </p>
    </div>

    <Section number="1" title="INFORMATION WE COLLECT">
      <p>We collect information you provide directly to us, such as when you:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Create an account</li>
        <li>Make a purchase</li>
        <li>Complete account setup</li>
        <li>Contact us for support</li>
        <li>Participate in Swap Spot activities or Tap Trades</li>
        <li>Use Augmented Reality (AR) features</li>
        <li>Open sealed packs or claim serial-numbered collectibles</li>
        <li>Enter challenges or stake Pack Points</li>
      </ul>
      <p className="mt-3">This may include: name, email, phone number, shipping address, payment information, device type, and AR usage analytics.</p>
    </Section>

    <Section number="2" title="HOW WE USE YOUR INFORMATION">
      <p>We use the information we collect to:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Operate, maintain, and improve our services</li>
        <li>Process transactions and send related information</li>
        <li>Comply with applicable regulations</li>
        <li>Detect and prevent fraud</li>
        <li>Communicate with you about products, services, and promotions</li>
        <li>Track AR view analytics (device type, duration, card viewed) to improve the AR experience</li>
        <li>Record trade history (Tap Trade, Event Trade, Remote Trade) for dispute resolution</li>
        <li>Maintain leaderboard rankings for Live Event Mode</li>
        <li>Process serial number claims and First Rip verification</li>
        <li>Comply with legal obligations</li>
      </ul>
    </Section>

    <Section number="3" title="INFORMATION SHARING">
      <p>We do not sell, trade, or rent your personal information to third parties. We may share information with:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Service providers who assist in operating our platform</li>
        <li>Payment processors for transaction processing</li>
        <li>Shipping carriers for order fulfillment</li>
        <li>Law enforcement when required by law</li>
      </ul>
    </Section>

    <Section number="4" title="DATA SECURITY">
      <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.</p>
    </Section>

    <Section number="5" title="YOUR RIGHTS">
      <p>You have the right to:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Access your personal information</li>
        <li>Correct inaccurate information</li>
        <li>Request instant deletion of your account (processed immediately from Profile settings)</li>
        <li>Opt out of marketing communications</li>
        <li>Request export of your data where required by applicable law</li>
      </ul>
      <p className="mt-3 text-gray-500 text-sm">Note: Upon account deletion, trade history and transaction records may be retained for a limited period as required for fraud prevention and legal compliance.</p>
    </Section>

    <Section number="6" title="AR & CAMERA DATA">
      <p>When you use Augmented Reality features, we access your device camera temporarily to render 3D models in your environment. We do <strong className="text-white">NOT</strong>:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Record, store, or transmit camera imagery or video</li>
        <li>Use facial recognition or biometric analysis</li>
        <li>Access your camera outside of active AR sessions</li>
      </ul>
      <p className="mt-3">We do collect anonymized AR usage analytics (device type, session duration, which card was viewed) to improve the AR experience.</p>
    </Section>

    <Section number="7" title="COOKIES & TRACKING">
      <p>We use cookies and similar technologies to enhance your experience, analyze usage, and assist in our marketing efforts. You can control cookie preferences through your browser settings.</p>
    </Section>

    <Section number="8" title="CONTACT US">
      <p>For privacy-related inquiries, contact us at: privacy@blkpacks.fun</p>
    </Section>

    <div className="mt-8 p-4 bg-gray-800/50 rounded-xl text-center">
      <p className="text-gray-500 text-sm">
        <strong className="text-white">BLK PACKS™</strong><br />
        205 North Michigan Avenue, Suite 810, Chicago, IL 60601
      </p>
    </div>
  </LegalPageWrapper>
);

export const CopyrightPage = () => (
  <LegalPageWrapper title="Copyright & DMCA" icon={Scale}>
    <div className="mb-6 p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl">
      <p className="text-orange-400 text-sm font-medium">
        BLK PACKS™ is a registered trademark and DBA of Xcraft FunWorks LLC
      </p>
    </div>

    <Section number="1" title="TRADEMARK NOTICE">
      <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-xl mb-4">
        <p className="text-orange-400 font-bold mb-2">BLK PACKS™ TRADEMARK PROTECTION</p>
        <p className="text-gray-400">The BLK PACKS™ name, logo, and associated branding are registered trademarks of Xcraft FunWorks LLC. Unauthorized use of the BLK PACKS™ trademark is strictly prohibited.</p>
      </div>
      <div className="space-y-3">
        <div className="p-3 bg-gray-800/50 rounded-lg">
          <p className="text-white font-bold text-sm">International Class 016 - Goods</p>
          <p className="text-gray-400 text-sm">Collectible printed trading cards; providing online non-downloadable digital collectible trading card images for entertainment purposes.</p>
        </div>
        <div className="p-3 bg-gray-800/50 rounded-lg">
          <p className="text-white font-bold text-sm">International Class 041 - Services</p>
          <p className="text-gray-400 text-sm">Providing online non-downloadable digital collectible trading card images and video highlights for entertainment purposes; entertainment services featuring digital collectibles in the nature of images and video recordings featuring historical, cultural, commemorative, and artistic themes.</p>
        </div>
      </div>
    </Section>

    <Section number="2" title="COPYRIGHT NOTICE">
      <p>All content on this Platform, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and software, is the property of BLK PACKS™ (Xcraft FunWorks LLC) or its content suppliers and is protected by United States and international copyright laws.</p>
    </Section>

    <Section number="3" title="DMCA COMPLIANCE">
      <p>We respect the intellectual property rights of others and comply with the Digital Millennium Copyright Act (DMCA). If you believe that your copyrighted work has been copied in a way that constitutes copyright infringement, please provide our DMCA Agent with the following information:</p>
      <ol className="list-decimal list-inside ml-4 space-y-2 mt-3">
        <li>A physical or electronic signature of the copyright owner or authorized agent</li>
        <li>Identification of the copyrighted work claimed to have been infringed</li>
        <li>Identification of the material that is claimed to be infringing</li>
        <li>Your contact information (address, telephone number, email)</li>
        <li>A statement that you have a good faith belief that the use is not authorized</li>
        <li>A statement, under penalty of perjury, that the information is accurate</li>
      </ol>
    </Section>

    <Section number="4" title="DMCA AGENT CONTACT">
      <div className="p-4 bg-gray-800/50 rounded-xl">
        <p className="text-white font-bold">DMCA Agent</p>
        <p>BLK PACKS™ (Xcraft FunWorks LLC)</p>
        <p>205 North Michigan Avenue, Suite 810</p>
        <p>Chicago, IL 60601</p>
        <p>Email: dmca@blkpacks.fun</p>
      </div>
    </Section>

    <Section number="4" title="COUNTER-NOTIFICATION">
      <p>If you believe your content was removed in error, you may submit a counter-notification with:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Your physical or electronic signature</li>
        <li>Identification of the removed material and its location</li>
        <li>A statement under penalty of perjury that you have a good faith belief the material was removed by mistake</li>
        <li>Your name, address, and telephone number</li>
        <li>Consent to jurisdiction of federal court</li>
      </ul>
    </Section>

    <Section number="5" title="REPEAT INFRINGERS">
      <p>We may terminate the accounts of users who are repeat infringers of intellectual property rights.</p>
    </Section>
  </LegalPageWrapper>
);

export const LicensingPage = () => (
  <LegalPageWrapper title="Licensing & Attribution" icon={Lock}>
    <Section number="1" title="PLATFORM LICENSE">
      <p>Subject to your compliance with these Terms, BLK PACKS™ grants you a limited, non-exclusive, non-transferable license to access and use the Platform for your personal, non-commercial use.</p>
    </Section>

    <Section number="2" title="DIGITAL COLLECTIBLE LICENSE">
      <p>When you purchase a digital collectible, you receive:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>A non-exclusive license to display the digital asset for personal use within the BLK PACKS™ platform</li>
        <li>The right to trade the digital collectible on our Swap Spot or via Tap Trade</li>
        <li>Access to AR experiences (3D models, animations) associated with AR-enabled cards</li>
        <li>The associated physical card (where applicable)</li>
        <li>Serial number verification for First Rip claims</li>
      </ul>
      <p className="mt-3 text-orange-400">You do NOT receive:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Copyright ownership of the underlying artwork or 3D models</li>
        <li>Commercial reproduction rights</li>
        <li>The right to create derivative works</li>
        <li>The right to download, extract, or redistribute 3D model files (GLB, USDZ)</li>
        <li>Ownership of AR tracking images or QR code mappings</li>
      </ul>
    </Section>

    <Section number="3" title="AR & 3D MODEL LICENSING">
      <p>All 3D models, animations, and AR-related assets displayed on the BLK PACKS™ platform are:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Proprietary assets of BLK PACKS™ (Xcraft FunWorks LLC) or licensed from third-party 3D artists</li>
        <li>Delivered exclusively through BLK PACKS™ servers — models cannot be downloaded, cached, or re-hosted</li>
        <li>Viewable only within the BLK PACKS™ platform, Apple AR Quick Look, or Google Scene Viewer as facilitated by the platform</li>
        <li>Subject to modification or removal at any time at our discretion</li>
      </ul>
    </Section>

    <Section number="4" title="PUBLIC DOMAIN CONTENT">
      <p>Certain designs may incorporate historical figures or elements in the public domain. The specific artistic expression and design compilation remain our intellectual property.</p>
    </Section>

    <Section number="5" title="THIRD-PARTY CONTENT">
      <p>The Platform may contain content licensed from third parties. Such content is used in accordance with applicable licensing agreements and remains the property of the respective owners.</p>
    </Section>

    <Section number="6" title="PROHIBITED USES">
      <p>You may NOT:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Use our intellectual property for commercial purposes without written consent</li>
        <li>Reproduce, distribute, or create derivative works</li>
        <li>Remove any copyright or proprietary notices</li>
        <li>Use automated systems to access the Platform</li>
        <li>Interfere with the proper working of the Platform</li>
      </ul>
    </Section>

    <Section number="6" title="ATTRIBUTION">
      <p>All artwork, designs, and content © 2026 BLK PACKS™. All rights reserved.</p>
    </Section>

    <Section number="7" title="THIRD-PARTY MARKS & USER-SCANNED CARDS">
      <div className="p-4 bg-orange-500/10 border-2 border-orange-500/40 rounded-xl mb-4">
        <p className="text-orange-400 font-black mb-2">BLK PACKS™ IS NOT A LICENSEE OF ANY THIRD-PARTY SPORTS IP</p>
        <p className="text-gray-200 text-sm leading-relaxed">
          BLK PACKS™ is interactive AR companion software. We do <strong className="text-white">not</strong> hold or claim a license from the NBA, NFL, MLB, NHL, MLS, NCAA, MLBPA, NFLPA, NBPA, Topps, Panini, Upper Deck, Bowman, Fleer, Donruss, Score, or any other professional sports league, players' association, team, broadcaster, manufacturer, or trading-card publisher. We do not issue or sublicense any third-party athlete name, image, or likeness (NIL).
        </p>
      </div>

      <p className="font-bold text-white mb-2">When the platform allows you to scan, register, or photograph a card you already own:</p>
      <ul className="list-disc list-inside ml-4 space-y-1 mb-4">
        <li>You are using BLK PACKS™ <strong className="text-white">software</strong> to enhance a card <strong className="text-white">you</strong> already lawfully own</li>
        <li>The third-party marks on that card (athlete names, team logos, league logos, manufacturer logos) remain the property of their respective owners — nothing in your use of BLK PACKS™ transfers, sublicenses, or expands those rights</li>
        <li>The BLK PACKS™ AR scene that plays around your card is a separately authored, transformative original work (animations, soundtrack, environment, UI) and is licensed only as described in Section 2 of this page</li>
      </ul>

      <p className="font-bold text-white mb-2">You may NOT:</p>
      <ul className="list-disc list-inside ml-4 space-y-1">
        <li>Extract, screen-capture, or rebroadcast BLK PACKS™ AR scenes for commercial use without our written consent</li>
        <li>Imply or represent that BLK PACKS™ holds a license from, is endorsed by, or is partnered with any third-party league, players' association, team, manufacturer, or athlete</li>
        <li>Use BLK PACKS™ AR scenes in any context that suggests official endorsement of a third-party IP</li>
      </ul>

      <p className="mt-3 text-gray-500 text-sm">All third-party trademarks remain the property of their respective owners. References to such marks on the platform are nominative and descriptive only.</p>
    </Section>

    <Section number="8" title="REPORTING IP CONCERNS">
      <p>If you are a rights holder and believe a specific BLK PACKS™ AR scene oversteps the transformative-software / nominative-fair-use posture, contact us at <a href="mailto:dmca@blkpacks.fun" className="text-orange-400 underline">dmca@blkpacks.fun</a> or follow the DMCA flow on the <a href="/copyright" className="text-orange-400 underline">Copyright</a> page. We review reports in good faith and may modify, gate, or disable the affected AR scene.</p>
    </Section>

    <div className="mt-8 p-4 bg-gray-800/50 rounded-xl text-center">
      <p className="text-gray-500 text-sm">
        For licensing inquiries, contact: licensing@blkpacks.fun
      </p>
    </div>
  </LegalPageWrapper>
);
